/**
 * FE-3a-SEARCH-1 — server-side ``GET /devices?q=`` search UX helpers.
 */

export const DEVICES_SEARCH_DEBOUNCE_MS = 350;
/** Must match API ``DEVICES_LIST_SEARCH_MIN_LEN`` (CAP-API-3a-004a). */
export const DEVICES_SEARCH_MIN_LEN = 2;

/**
 * @param {string} q
 * @returns {boolean}
 */
export function devicesSearchQueryReady(q) {
  const s = String(q || "").trim();
  if (!s) return false;
  return s.length >= DEVICES_SEARCH_MIN_LEN;
}

/**
 * @param {string} q
 * @returns {boolean}
 */
export function devicesSearchTooShort(q) {
  const s = String(q || "").trim();
  return s.length > 0 && s.length < DEVICES_SEARCH_MIN_LEN;
}

/**
 * @param {string} q
 * @returns {boolean}
 */
export function devicesServerSearchActive(q) {
  return devicesSearchQueryReady(q);
}

/**
 * @param {string} [bannerId]
 * @returns {string}
 */
export function devicesSearchMinLenBannerHtml(bannerId = "devicesSearchMinLenBanner") {
  return (
    `<p class="badge partial" id="${bannerId}" data-devices-search-min-len="1" style="margin:0 0 8px">` +
    `Enter at least ${DEVICES_SEARCH_MIN_LEN} characters for server-side device search (万级读保护). Shorter input filters the loaded page only.` +
    `</p>`
  );
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {boolean} active
 * @param {string} [bannerId]
 */
export function syncDevicesSearchMinLenBanner(view, active, bannerId = "devicesSearchMinLenBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  if (!active) {
    if (existing) existing.remove();
    return;
  }
  const html = devicesSearchMinLenBannerHtml(bannerId);
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("#allDevFilter")?.closest(".card") || view.querySelector(".card");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}

/**
 * @param {string} [bannerId]
 * @returns {string}
 */
export function devicesServerSearchBannerHtml(bannerId = "devicesSearchServerBanner") {
  return (
    `<p class="badge accent" id="${bannerId}" data-devices-server-search="1" style="margin:0 0 8px">` +
    `Search is on — results come in pages. Use Load more for additional matches.` +
    `</p>`
  );
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {boolean} active
 * @param {string} [bannerId]
 */
export function syncDevicesServerSearchBanner(view, active, bannerId = "devicesSearchServerBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  if (!active) {
    if (existing) existing.remove();
    return;
  }
  const html = devicesServerSearchBannerHtml(bannerId);
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("#allDevFilter")?.closest(".card") || view.querySelector(".card");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}

/**
 * @param {(q: string) => void} fn
 * @param {number} [ms]
 * @returns {(q: string) => void}
 */
export function debounceDevicesSearch(fn, ms = DEVICES_SEARCH_DEBOUNCE_MS) {
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;
  return (q) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(String(q || ""));
    }, ms);
  };
}
